import { getAudioCtx } from "./audio";
import { setOrbLevel, setOrbState } from "./orb";

const VOICE_RMS = 0.035;
const BARGE_RMS = 0.08;
const BARGE_FRAMES = 14;

// Records one utterance with voice-activity detection. Resolves with a Blob or null (no speech).
// While holdWhile() is true (Aurora is talking) it only waits for a loud, sustained voice (barge-in).
export async function recordUtterance({ signal, silenceMs = 1000, maxMs = 30000, idleMs = 9000, holdWhile, onBargeIn } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
  const ac = getAudioCtx();
  await ac.resume().catch(() => {});
  const src = ac.createMediaStreamSource(stream);
  const analyser = ac.createAnalyser();
  analyser.fftSize = 512;
  src.connect(analyser);
  const mime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((m) => window.MediaRecorder?.isTypeSupported?.(m)) || "";
  const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);

  return new Promise((resolve) => {
    const buf = new Uint8Array(analyser.fftSize);
    let recording = false, heard = false, stopped = false, loud = 0;
    let since = performance.now(), lastVoice = since, raf;
    const cleanup = () => { stream.getTracks().forEach((t) => t.stop()); src.disconnect(); };
    const begin = (t) => { recording = true; since = t; rec.start(250); setOrbState("ascolto"); };
    const finish = () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      if (recording && rec.state !== "inactive") rec.stop();
      else { cleanup(); resolve(null); }
    };
    rec.onstop = () => {
      cleanup();
      setOrbLevel(0);
      resolve(heard && chunks.length ? new Blob(chunks, { type: rec.mimeType || "audio/webm" }) : null);
    };
    const tick = () => {
      if (signal?.aborted) return finish();
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
      const rms = Math.sqrt(sum / buf.length);
      const t = performance.now();
      if (!recording) {
        loud = rms > BARGE_RMS ? loud + 1 : 0;
        if (loud >= BARGE_FRAMES) { onBargeIn?.(); begin(t); heard = true; lastVoice = t; }
        else if (!holdWhile?.()) begin(t);
        raf = requestAnimationFrame(tick);
        return;
      }
      setOrbLevel(Math.min(1, rms * 7));
      if (rms > VOICE_RMS) { heard = true; lastVoice = t; }
      if ((heard && t - lastVoice > silenceMs) || t - since > maxMs || (!heard && t - since > idleMs)) return finish();
      raf = requestAnimationFrame(tick);
    };
    signal?.addEventListener("abort", finish);
    tick();
  });
}

export const blobExt = (b) => (b.type.includes("mp4") ? "m4a" : "webm");
